/**
 * @file frontend/src/pages/auth/AuthCallback.js
 * @module auth
 * @component AuthCallback
 *
 * Auth Callback — Top-level page rendered by the React Router.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AuthCallback = () => {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Invalid authentication response');
          navigate('/login');
          return;
        }

        // Process the session; userData may include default_route and
        // tfa_setup_required so we can redirect the user to the correct
        // dashboard — the same way the standard email/password login does.
        const userData = await processGoogleSession(sessionId);
        toast.success('Welcome!');

        // Clear the hash before navigating
        window.history.replaceState(null, '', window.location.pathname);

        if (userData?.tfa_setup_required) {
          navigate('/setup-2fa', { replace: true });
          return;
        }
        const targetRoute = userData?.default_route || '/dashboard';
        navigate(targetRoute, { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processSession();
  }, [processGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-medium">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
